import type { ContestType } from '@betterpool/api-types/contests-service';
import type { ContestSlate } from '@betterpool/api-types/games-service';

import { createQueryString } from '~/domains/common/utils/queryString';
import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import { type Slate } from '~/services/games-service/interfaces/slates.interface';

import type { GameDetail } from '../interfaces/gameDetail.interface';
import type { PlayerDetail } from '../interfaces/player-detail.interface';
import type { APIPlayerSeasonGame, PlayerSeasonGame } from '../interfaces/player-season.interface';
import type { Slate as OldSlate } from '../interfaces/slates.interface';

import gamesServiceInstance from './games-service.service.instance';
import parsePlayerSeasonData from './parsers/parsePlayerSeasonData';

const getSlates = async (scheduleId: string, authToken?: string): Promise<OldSlate[]> => {
  const response = await gamesServiceInstance.get<OldSlate[]>(`/schedules/${scheduleId}/slates`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  return response.data;
};

const getContestSlates = async ({
  scheduleId,
  contestType,
  finalSlateId,
  authToken,
  offset,
  limit,
}: {
  scheduleId: string;
  contestType: ContestType;
  finalSlateId?: string;
  authToken?: string;
  offset?: number;
  limit?: number;
}): Promise<PaginatedData<ContestSlate & { startDate: string }>> => {
  const response = await gamesServiceInstance.get<
    PaginatedData<ContestSlate & { startDate: string }>
  >(
    `/slates/schedules/${scheduleId}${createQueryString({
      contestType,
      finalSlateId,
      offset,
      limit,
    })}`,
    {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
    }
  );
  return response.data;
};

const getSlate = async (slateId: string, authToken?: string): Promise<Slate> => {
  const response = await gamesServiceInstance.get<Slate>(`/slates/${slateId}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  return response.data;
};

const getSlateWithGames = async (slateId: string, authToken?: string): Promise<Slate> => {
  const response = await gamesServiceInstance.get<Slate>(`/slates/${slateId}?include=games`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  return response.data;
};

const getGameDetail = async (gameId: string, contestId?: string): Promise<GameDetail> => {
  const query = createQueryString({
    contestId,
  });

  const response = await gamesServiceInstance.get<GameDetail>(`/games/${gameId}${query}`);
  return response.data;
};

type GameTeeTimes = {
  round: number;
  teetimes?: {
    playerName: string;
    playerId: string;
    teetime: string;
  }[];
};

const getGameTeeTimes = async ({
  gameId,
  round = 1,
}: {
  gameId: string;
  round?: number;
}): Promise<GameTeeTimes> => {
  const query = createQueryString({ round });
  const response = await gamesServiceInstance.get<GameTeeTimes>(`/games/${gameId}/teetimes${query}`);
  return response.data;
};

const getPlayerDetail = async ({
  playerId,
  slateId,
  seasonId,
  contestId,
}: {
  playerId: string;
  slateId?: string;
  seasonId?: string;
  contestId?: string;
}): Promise<PlayerDetail> => {
  const query = createQueryString({
    slateId,
    seasonId,
    contestId,
  });
  const response = await gamesServiceInstance.get<PlayerDetail>(`/players/${playerId}${query}`);
  return response.data;
};

const getPlayerSeasonData = async ({
  playerId,
  seasonId,
  limit,
  offset,
  seasonTypes = ['REG', 'POST'],
}: {
  playerId: string;
  seasonId?: string;
  limit?: number;
  offset?: number;
  seasonTypes?: ('REG' | 'POST' | 'PRE')[];
}): Promise<PaginatedData<PlayerSeasonGame>> => {
  const query = createQueryString({
    seasonId,
    limit,
    offset,
    seasonTypes,
  });
  const response = await gamesServiceInstance.get<PaginatedData<APIPlayerSeasonGame>>(
    `/players/${playerId}/player-activities${query}`
  );

  return {
    ...response.data,
    data: parsePlayerSeasonData(response.data?.data ?? []),
  };
};

export {
  getGameDetail,
  getGameTeeTimes,
  getPlayerDetail,
  getPlayerSeasonData,
  getSlate,
  getSlates,
  getSlateWithGames,
  getContestSlates,
};
